/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Platform } from 'react-native';
import { CurrencyConversion } from '../types/FlightGuestAggregator.types';
import { REWARDS_CURRENCY } from '../components/Price/Constant';

export const convertCurrencyAmount = (
  amount: number | string | null | undefined,
  exchangeRate: number,
): number => {
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount)) {
    return 0;
  }

  if (!Number.isFinite(exchangeRate)) {
    return numericAmount;
  }

  return Number((numericAmount * exchangeRate).toFixed(2));
};

export const getPrice = (
  amount: number | string | null | undefined,
  userCurrency: string,
  currencyResponse: CurrencyConversion | undefined,
) => {
  const { currencyCode: fareCurrency, conversionRates } =
    currencyResponse || {};
  const formattedUserCurrency = userCurrency?.toUpperCase() ?? '';
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount)) {
    return undefined;
  }

  let priceToShow: undefined | string = '';
  if (!currencyResponse || formattedUserCurrency === fareCurrency) {
    priceToShow = numericAmount.toFixed(2);
  } else {
    const rate = conversionRates?.[formattedUserCurrency];
    if (rate !== undefined) {
      priceToShow = (numericAmount * rate).toFixed(2);
    } else {
      priceToShow = numericAmount.toFixed(2);
    }
  }

  return priceToShow;
};

export const getDisplayCurrency = (
  userCurrency: string,
  currencyResponse: CurrencyConversion | undefined,
) => {
  const { currencyCode: fareCurrency, conversionRates } =
    currencyResponse || {};
  const formattedUserCurrency = userCurrency?.toUpperCase() ?? '';

  if (!currencyResponse || formattedUserCurrency === fareCurrency) {
    return fareCurrency || formattedUserCurrency;
  }

  if (conversionRates?.[formattedUserCurrency] !== undefined) {
    return formattedUserCurrency;
  }

  return fareCurrency || formattedUserCurrency;
};

/**
 * Returns the user-facing display currency.
 * For mobile native platforms (iOS/Android), LPX maps to PTS.
 */
export const getDisplayCurrencyForUI = (currency?: string): string => {
  if (!currency) return '';
  const upper = currency.toUpperCase();
  const isNative = Platform.OS === 'ios' || Platform.OS === 'android';

  if (isNative && upper === REWARDS_CURRENCY[1]) {
    return REWARDS_CURRENCY[0];
  }
  return upper;
};

/**
 * Formats a final currency string with the correct position.
 * For PTS, the currency symbol is placed on the right (e.g., '20,000 PTS').
 * For fiat currencies, it is placed on the left (e.g., 'MYR 20.00').
 */
export const formatCurrencyString = (
  amountString: string | number,
  currency?: string,
): string => {
  if (!currency) return String(amountString);
  const displayCurrency = getDisplayCurrencyForUI(currency);

  if (displayCurrency === REWARDS_CURRENCY[0]) {
    return `${amountString} ${displayCurrency}`;
  }
  return `${displayCurrency} ${amountString}`;
};
